// sim/wildlife.ts — the WILDLIFE AI system + species behaviour templates.
//
// Wildlife is NON-COLONY life (KIND.Wildlife). Like the caste layer, a species is
// a stateless, allocation-free BEHAVIOUR TEMPLATE shared by every unit of that
// species (one singleton each in SPECIES_BEHAVIORS), dispatched by a fixed lookup
// on the species id — the per-tick loop never allocates a closure/object per unit.
//
//   Spider    territorial ambush: defends a fixed home radius, never roams far
//   Bird      flying migratory hunter: ranges across the map, ignores walls
//   Rat       ground omnivore + colony raider: hunts, steals food, swarms anchors
//   Frog      a tighter, weaker Spider (shares the ambush behaviour)
//   Beetle    armoured ground scavenger: ignores insects, raids food nodes near home
//   Dragonfly fast flying TERRITORIAL hunter: darts on insects within its home radius
//
// COLONY-INTERACTION SEAM: wildlife depends ONLY on the small interfaces below
// (ColonyPresence / PreyKillSink / RaidableFood), never on ColonySystem /
// ColonyAISystem directly. So this layer runs WITH colonies (anchors drive Rat
// raids; kills fix the colony census; raids deplete the shared food store) or
// entirely WITHOUT them. THIS is "interaction rules with colonies".
//
// DETERMINISM: pure integer state (exact FNV hashing), fixed iteration (snapshot
// the active set; box scans visit tiles in (y,x) order, ties break on lower id),
// RNG only from ctx. Allocation-free: a reused scratch snapshot, no per-tick temp
// objects/closures. Sensing = bounded Chebyshev box scans over grid buckets.

import {
  DIR,
  DIR_DX,
  DIR_DY,
  DIR_COUNT,
  NIL,
  KIND,
  SPECIES,
  SPECIES_COUNT,
  WILDLIFE_STATE,
} from '../types.ts';
import type { EntityId, Species, Tick, System, SpeciesConfig } from '../types.ts';
import type { EntitySystem } from '../engine/EntitySystem.ts';
import type { WorldGrid } from '../engine/WorldGrid.ts';
import type { Rng } from '../rng.ts';
import type { GreedyPathing } from '../engine/Pathing.ts';
import { chebyshev } from '../engine/Pathing.ts';

// ---------------------------------------------------------------------------
// Colony-interaction seam — the ONLY coupling wildlife has to "the rest".
// ---------------------------------------------------------------------------

/** A read-only view of colony "anchors" (nests + hive nodes) wildlife reacts to. */
export interface ColonyPresence {
  /** Number of colony anchors on the map (0 ⇒ no colonies). */
  anchorCount(): number;
  /** X of anchor `i` in [0, anchorCount). */
  anchorX(i: number): number;
  /** Y of anchor `i` in [0, anchorCount). */
  anchorY(i: number): number;
}

/** Sink that fixes external bookkeeping when wildlife kills a colony organism. */
export interface PreyKillSink {
  /** Called BEFORE the organism is despawned (the sink only fixes the census). */
  onOrganismKilled(id: EntityId): void;
}

/** Seam for raiding a food node: take up to `amount`, return the amount taken. */
export interface RaidableFood {
  raid(id: EntityId, amount: number): number;
}

/**
 * Everything a wildlife behaviour needs, assembled ONCE per world and passed by
 * reference to every update call. Never rebuilt per tick or per unit. The colony
 * seams are optional — absent ⇒ wildlife runs standalone.
 */
export interface WildlifeCtx {
  readonly entities: EntitySystem;
  readonly grid: WorldGrid;
  readonly rng: Rng;
  readonly pathing: GreedyPathing;
  /** Colony anchors wildlife reacts to (Rat raids). Absent ⇒ no colonies. */
  readonly presence?: ColonyPresence;
  /** Fix the colony census when wildlife kills an organism. Absent ⇒ skipped. */
  readonly preyKill?: PreyKillSink;
  /** Food-node raid seam (Rat). Absent ⇒ wildlife can't eat food nodes. */
  readonly food?: RaidableFood;
}

/** A species behaviour template: stateless, deterministic, allocation-free. */
export interface WildlifeBehavior {
  readonly species: Species;
  readonly name: string;
  /** Advance wildlife unit `id` by one tick. Must not allocate. */
  update(sys: WildlifeSystem, id: EntityId, tick: Tick): void;
}

/** Food units a raider tries to take per food-node bite. */
const RAID_BITE = 2;

export class WildlifeSystem implements System {
  readonly name = 'wildlife';

  // Public-by-design so the module-level behaviour templates can read them
  // WITHOUT allocating (no per-tick wrapper objects). They are never reassigned.
  readonly ctx: WildlifeCtx;
  readonly entities: EntitySystem;
  readonly grid: WorldGrid;
  readonly rng: Rng;
  readonly pathing: GreedyPathing;
  private readonly configs: readonly SpeciesConfig[];

  // --- Per-unit columns (indexed by EntityId). Reuse the shared SoA for the
  //     generic fields (pos/energy/hp/state/target); these are wildlife-only. ---
  /** Species id (SPECIES.*) for KIND.Wildlife; unused for other kinds. */
  readonly species: Uint8Array;
  /** Home tile X (the ambush territory centre / spawn point). */
  readonly homeX: Int32Array;
  /** Home tile Y. */
  readonly homeY: Int32Array;

  /** Reused active-set snapshot (no per-tick allocation). */
  private readonly scratch: Int32Array;

  /** Live population per species, indexed by SPECIES.*. */
  readonly popBySpecies: Int32Array;
  /** Total live wildlife. */
  wildlifeCount = 0;

  // --- Public counters (deterministic; folded into the hash, read by tests). ---
  spawnedTotal = 0;
  predationKills = 0;
  foodRaided = 0;
  starvedTotal = 0;

  constructor(ctx: WildlifeCtx, configs: readonly SpeciesConfig[]) {
    this.ctx = ctx;
    this.entities = ctx.entities;
    this.grid = ctx.grid;
    this.rng = ctx.rng;
    this.pathing = ctx.pathing;
    this.configs = configs;

    const cap = ctx.entities.capacity;
    this.species = new Uint8Array(cap);
    this.homeX = new Int32Array(cap);
    this.homeY = new Int32Array(cap);
    this.scratch = new Int32Array(cap);
    this.popBySpecies = new Int32Array(SPECIES_COUNT);
  }

  // --- Lifecycle -------------------------------------------------------------

  /**
   * Spawn one wildlife unit of `species` at (x,y), fully initialised from the
   * species config and placed on the grid. O(1), allocation-free.
   */
  spawnWildlife(species: Species, x: number, y: number): EntityId {
    const e = this.entities;
    const cfg = this.configs[species] as SpeciesConfig;
    const id = e.spawn(KIND.Wildlife, x, y);
    this.species[id] = species;
    this.homeX[id] = x;
    this.homeY[id] = y;
    e.energy[id] = cfg.startEnergy;
    e.hp[id] = cfg.startHp;
    e.state[id] = WILDLIFE_STATE.Roaming;
    e.targetX[id] = x;
    e.targetY[id] = y;
    // Flyers don't occupy tiles (don't count toward the per-tile cap).
    e.blocks[id] = cfg.flying ? 0 : 1;
    this.grid.add(id);

    this.popBySpecies[species] = (this.popBySpecies[species] as number) + 1;
    this.wildlifeCount++;
    this.spawnedTotal++;
    return id;
  }

  /** Remove a wildlife unit from the grid + entity store (starvation/death). */
  killWildlife(id: EntityId): void {
    const sp = this.species[id] as number;
    this.grid.remove(id);
    this.entities.despawn(id);
    const p = this.popBySpecies[sp] as number;
    if (p > 0) this.popBySpecies[sp] = p - 1;
    if (this.wildlifeCount > 0) this.wildlifeCount--;
  }

  /** Total live wildlife. */
  population(): number {
    return this.wildlifeCount;
  }
  /** Live population of one species. */
  populationOf(species: Species): number {
    return this.popBySpecies[species] as number;
  }

  // --- Behaviour helpers (all allocation-free) -------------------------------

  /** This unit's species config. */
  cfgOf(id: EntityId): SpeciesConfig {
    return this.configs[this.species[id] as number] as SpeciesConfig;
  }
  homeXOf(id: EntityId): number {
    return this.homeX[id] as number;
  }
  homeYOf(id: EntityId): number {
    return this.homeY[id] as number;
  }

  /**
   * Nearest live prey (KIND.Ant OR KIND.Organism) within Chebyshev radius r of
   * (ax,ay), or NIL. Box scan over grid buckets in (y,x) order; ties break on the
   * lower entity id. Never an O(n^2) all-pairs scan.
   */
  sensePrey(ax: number, ay: number, r: number): EntityId {
    const e = this.entities;
    const grid = this.grid;
    let bestId: EntityId = NIL;
    let bestDist = r + 1;
    let bestTieId = 0x7fffffff;

    const minX = ax - r < 0 ? 0 : ax - r;
    const minY = ay - r < 0 ? 0 : ay - r;
    const maxX = ax + r >= grid.width ? grid.width - 1 : ax + r;
    const maxY = ay + r >= grid.height ? grid.height - 1 : ay + r;

    for (let ty = minY; ty <= maxY; ty++) {
      for (let tx = minX; tx <= maxX; tx++) {
        const ti = grid.index(tx, ty);
        for (let cur = grid.headAt(ti); cur !== NIL; cur = grid.next(cur)) {
          const c = cur as number;
          const k = e.kind[c] as number;
          if (k !== KIND.Ant && k !== KIND.Organism) continue;
          // Organisms track hp; a slain-but-not-yet-despawned one isn't prey.
          if (k === KIND.Organism && (e.hp[c] as number) <= 0) continue;
          const dist = chebyshev(ax, ay, tx, ty);
          if (dist < bestDist || (dist === bestDist && c < bestTieId)) {
            bestDist = dist;
            bestId = cur;
            bestTieId = c;
          }
        }
      }
    }
    return bestId;
  }

  /** Nearest on-grid food node (amount > 0) within radius r, or NIL. */
  senseFoodNode(ax: number, ay: number, r: number): EntityId {
    const e = this.entities;
    const grid = this.grid;
    let bestId: EntityId = NIL;
    let bestDist = r + 1;
    let bestTieId = 0x7fffffff;

    const minX = ax - r < 0 ? 0 : ax - r;
    const minY = ay - r < 0 ? 0 : ay - r;
    const maxX = ax + r >= grid.width ? grid.width - 1 : ax + r;
    const maxY = ay + r >= grid.height ? grid.height - 1 : ay + r;

    for (let ty = minY; ty <= maxY; ty++) {
      for (let tx = minX; tx <= maxX; tx++) {
        const ti = grid.index(tx, ty);
        for (let cur = grid.headAt(ti); cur !== NIL; cur = grid.next(cur)) {
          const c = cur as number;
          if ((e.kind[c] as number) !== KIND.Food) continue;
          if ((e.foodAmount[c] as number) <= 0) continue;
          const dist = chebyshev(ax, ay, tx, ty);
          if (dist < bestDist || (dist === bestDist && c < bestTieId)) {
            bestDist = dist;
            bestId = cur;
            bestTieId = c;
          }
        }
      }
    }
    return bestId;
  }

  /** True if a food-raid seam is wired (Rat can eat food nodes). */
  hasFoodSeam(): boolean {
    return this.ctx.food !== undefined;
  }

  /**
   * Index of the nearest colony anchor to (ax,ay), or NIL if there are none.
   * Linear scan over the (few) anchors in registration order; ties break on the
   * lower index. The presence seam is the colony-expansion signal.
   */
  nearestAnchor(ax: number, ay: number): number {
    const presence = this.ctx.presence;
    if (presence === undefined) return NIL;
    const count = presence.anchorCount();
    if (count <= 0) return NIL;
    let bestI = NIL;
    let bestDist = 0x7fffffff;
    for (let i = 0; i < count; i++) {
      const dist = chebyshev(ax, ay, presence.anchorX(i), presence.anchorY(i));
      if (dist < bestDist) {
        bestDist = dist;
        bestI = i;
      }
    }
    return bestI;
  }
  /** X of anchor index `i` (caller has checked it's valid). */
  anchorXAt(i: number): number {
    return (this.ctx.presence as ColonyPresence).anchorX(i);
  }
  /** Y of anchor index `i`. */
  anchorYAt(i: number): number {
    return (this.ctx.presence as ColonyPresence).anchorY(i);
  }

  /**
   * One greedy step toward (tx,ty). Ground movement respects walls + the per-tile
   * cap (via pathing); flying is bounds-only (ignores walls/crowds). Returns moved.
   * MIRRORS castes.ts stepOnce.
   */
  private stepOnce(id: EntityId, tx: number, ty: number, flying: boolean): boolean {
    const e = this.entities;
    const ax = e.posX[id] as number;
    const ay = e.posY[id] as number;
    if (ax === tx && ay === ty) return false;
    if (!flying) {
      const dir = this.pathing.step(ax, ay, tx, ty);
      if (dir === DIR.Stay) return false;
      this.grid.move(id, ax + (DIR_DX[dir] as number), ay + (DIR_DY[dir] as number));
      return true;
    }
    // Flying: pick the bounds-admissible neighbour that most reduces distance.
    let bestDir: number = DIR.Stay;
    let bestDist = chebyshev(ax, ay, tx, ty);
    for (let d = 0; d < DIR_COUNT; d++) {
      const nx = ax + (DIR_DX[d] as number);
      const ny = ay + (DIR_DY[d] as number);
      if (!this.grid.inBounds(nx, ny)) continue;
      const dist = chebyshev(nx, ny, tx, ty);
      if (dist < bestDist) {
        bestDist = dist;
        bestDir = d;
      }
    }
    if (bestDir === DIR.Stay) return false;
    this.grid.move(id, ax + (DIR_DX[bestDir] as number), ay + (DIR_DY[bestDir] as number));
    return true;
  }

  /**
   * Advance up to `cfg.stepsPerTick` steps toward (tx,ty). Stops early on arrival
   * or when blocked. Returns whether it moved at all. MIRRORS castes.ts advance.
   */
  advance(id: EntityId, tx: number, ty: number, cfg: SpeciesConfig): boolean {
    const e = this.entities;
    let moved = false;
    for (let s = 0; s < cfg.stepsPerTick; s++) {
      if (!this.stepOnce(id, tx, ty, cfg.flying)) break;
      moved = true;
      if ((e.posX[id] as number) === tx && (e.posY[id] as number) === ty) break;
    }
    return moved;
  }

  /** One deterministic random step (drift). Honours flight. MIRRORS castes.ts wander. */
  wander(id: EntityId, cfg: SpeciesConfig): void {
    const e = this.entities;
    const grid = this.grid;
    const ax = e.posX[id] as number;
    const ay = e.posY[id] as number;
    const start = this.rng.int(DIR_COUNT);
    for (let k = 0; k < DIR_COUNT; k++) {
      const d = (start + k) % DIR_COUNT;
      const nx = ax + (DIR_DX[d] as number);
      const ny = ay + (DIR_DY[d] as number);
      const ok = cfg.flying ? grid.inBounds(nx, ny) : grid.canMoveAnt(nx, ny);
      if (ok) {
        grid.move(id, nx, ny);
        return;
      }
    }
  }

  /** Gain feed energy, capped at twice the species' start energy. */
  feed(id: EntityId, cfg: SpeciesConfig): void {
    const e = this.entities;
    const cap = 2 * cfg.startEnergy;
    const next = (e.energy[id] as number) + cfg.feedEnergy;
    e.energy[id] = next > cap ? cap : next;
  }

  /**
   * Strike adjacent prey. An Organism takes attackPower damage and dies at hp<=0
   * (the preyKill sink fixes the colony census BEFORE despawn); an Ant dies
   * outright. On a kill: count it, despawn the prey, and feed the predator.
   */
  strikePrey(id: EntityId, preyId: EntityId, cfg: SpeciesConfig): void {
    const e = this.entities;
    const k = e.kind[preyId] as number;
    if (k === KIND.Organism) {
      const hp = (e.hp[preyId] as number) - cfg.attackPower;
      e.hp[preyId] = hp;
      if (hp <= 0) {
        this.ctx.preyKill?.onOrganismKilled(preyId);
        this.grid.remove(preyId);
        e.despawn(preyId);
        this.predationKills++;
        this.feed(id, cfg);
      }
    } else if (k === KIND.Ant) {
      this.grid.remove(preyId);
      e.despawn(preyId);
      this.predationKills++;
      this.feed(id, cfg);
    }
  }

  /** Raid an adjacent food node through the RaidableFood seam; feed on success. */
  eatFood(id: EntityId, foodId: EntityId, cfg: SpeciesConfig): void {
    const taken = this.ctx.food?.raid(foodId, RAID_BITE) ?? 0;
    if (taken > 0) {
      this.foodRaided += taken;
      this.feed(id, cfg);
    }
  }

  // --- Per-tick: dispatch every wildlife unit to its species behaviour --------

  update(tick: Tick): void {
    const e = this.entities;

    // Snapshot the active set so despawns (predation/starvation) during the tick
    // are safe and processing order is deterministic.
    const active = e.activeView();
    const n = e.aliveCount;
    const scratch = this.scratch;
    for (let i = 0; i < n; i++) scratch[i] = active[i] as number;

    for (let i = 0; i < n; i++) {
      const id = scratch[i] as number;
      if (e.alive[id] === 0) continue; // killed earlier this tick
      if ((e.kind[id] as number) !== KIND.Wildlife) continue;

      const sp = this.species[id] as number;
      (SPECIES_BEHAVIORS[sp] as WildlifeBehavior).update(this, id, tick);
      if (e.alive[id] === 0) continue; // (defensive) behaviour despawned it

      // Energy upkeep + death checks (shared across species).
      const cfg = this.configs[sp] as SpeciesConfig;
      const energy = (e.energy[id] as number) - cfg.energyDecay;
      e.energy[id] = energy;
      if (energy <= 0) {
        this.starvedTotal++;
        this.killWildlife(id);
      } else if ((e.hp[id] as number) <= 0) {
        this.killWildlife(id);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Species behaviour templates (stateless singletons).
// Behaviours read ONLY the public surface of WildlifeSystem (sys.entities,
// sys.grid, sys.rng, the helpers above) — no allocation, no private reach-in.
// ---------------------------------------------------------------------------

/**
 * Shared ambush behaviour for Spider + Frog: defend a fixed territory around home.
 * Engage prey ONLY if it lies within `territoryRadius` of home — never chase
 * distant prey out of the territory. With nothing to defend, drift back toward
 * home (or wander one step when already there).
 */
function ambush(sys: WildlifeSystem, id: EntityId): void {
  const e = sys.entities;
  const cfg = sys.cfgOf(id);
  const ax = e.posX[id] as number;
  const ay = e.posY[id] as number;
  const hx = sys.homeXOf(id);
  const hy = sys.homeYOf(id);

  const prey = sys.sensePrey(ax, ay, cfg.senseRadius);
  if (prey !== NIL) {
    const px = e.posX[prey] as number;
    const py = e.posY[prey] as number;
    // Engage ONLY prey inside the territory — never chase distant prey out.
    if (chebyshev(hx, hy, px, py) <= cfg.territoryRadius) {
      e.state[id] = WILDLIFE_STATE.Defending;
      if (chebyshev(ax, ay, px, py) <= 1) sys.strikePrey(id, prey, cfg);
      else sys.advance(id, px, py, cfg);
      return;
    }
  }
  // Nothing engageable: drift home (or wander one step when already there).
  e.state[id] = WILDLIFE_STATE.Roaming;
  if (chebyshev(ax, ay, hx, hy) > 1) sys.advance(id, hx, hy, cfg);
  else sys.wander(id, cfg);
}

const SpiderBehavior: WildlifeBehavior = {
  species: SPECIES.Spider,
  name: 'spider',
  update(sys, id) {
    ambush(sys, id);
  },
};

const FrogBehavior: WildlifeBehavior = {
  species: SPECIES.Frog,
  name: 'frog',
  update(sys, id) {
    ambush(sys, id);
  },
};

const BirdBehavior: WildlifeBehavior = {
  species: SPECIES.Bird,
  name: 'bird',
  update(sys, id, tick) {
    const e = sys.entities;
    const cfg = sys.cfgOf(id);
    const ax = e.posX[id] as number;
    const ay = e.posY[id] as number;
    const prey = sys.sensePrey(ax, ay, cfg.senseRadius);
    if (prey !== NIL) {
      e.state[id] = WILDLIFE_STATE.Hunting;
      const px = e.posX[prey] as number;
      const py = e.posY[prey] as number;
      if (chebyshev(ax, ay, px, py) <= 1) sys.strikePrey(id, prey, cfg);
      else sys.advance(id, px, py, cfg);
      return;
    }
    // No prey: migrate. Pick a fresh far target on the cadence or on arrival.
    e.state[id] = WILDLIFE_STATE.Migrating;
    const interval = cfg.migrateInterval > 0 ? cfg.migrateInterval : 1;
    const atTarget = ax === (e.targetX[id] as number) && ay === (e.targetY[id] as number);
    if ((tick + id) % interval === 0 || atTarget) {
      e.targetX[id] = sys.rng.int(sys.grid.width);
      e.targetY[id] = sys.rng.int(sys.grid.height);
    }
    sys.advance(id, e.targetX[id] as number, e.targetY[id] as number, cfg);
  },
};

const RatBehavior: WildlifeBehavior = {
  species: SPECIES.Rat,
  name: 'rat',
  update(sys, id) {
    const e = sys.entities;
    const cfg = sys.cfgOf(id);
    const ax = e.posX[id] as number;
    const ay = e.posY[id] as number;

    const prey = cfg.eatsInsects ? sys.sensePrey(ax, ay, cfg.senseRadius) : NIL;
    const food =
      cfg.eatsFood && sys.hasFoodSeam() ? sys.senseFoodNode(ax, ay, cfg.senseRadius) : NIL;

    // Adjacent prey → strike; adjacent food → raid (prey takes priority).
    if (prey !== NIL && chebyshev(ax, ay, e.posX[prey] as number, e.posY[prey] as number) <= 1) {
      e.state[id] = WILDLIFE_STATE.Hunting;
      sys.strikePrey(id, prey, cfg);
      return;
    }
    if (food !== NIL && chebyshev(ax, ay, e.posX[food] as number, e.posY[food] as number) <= 1) {
      e.state[id] = WILDLIFE_STATE.Feeding;
      sys.eatFood(id, food, cfg);
      return;
    }

    // Pick the nearer of {prey, food} and close on it.
    let tx = -1;
    let ty = -1;
    let have = false;
    if (prey !== NIL) {
      tx = e.posX[prey] as number;
      ty = e.posY[prey] as number;
      have = true;
    }
    if (food !== NIL) {
      const fx = e.posX[food] as number;
      const fy = e.posY[food] as number;
      if (!have || chebyshev(ax, ay, fx, fy) < chebyshev(ax, ay, tx, ty)) {
        tx = fx;
        ty = fy;
        have = true;
      }
    }
    if (have) {
      e.state[id] = WILDLIFE_STATE.Hunting;
      sys.advance(id, tx, ty, cfg);
      return;
    }

    // No prey/food sensed: head for a colony anchor to raid (reacts to colony
    // expansion); otherwise just roam. The anchor target = colony interaction.
    const a = sys.nearestAnchor(ax, ay);
    if (a !== NIL) {
      e.state[id] = WILDLIFE_STATE.Migrating;
      const cx = sys.anchorXAt(a);
      const cy = sys.anchorYAt(a);
      if (chebyshev(ax, ay, cx, cy) > cfg.roamRadius) sys.advance(id, cx, cy, cfg);
      else sys.wander(id, cfg);
      return;
    }
    e.state[id] = WILDLIFE_STATE.Roaming;
    sys.wander(id, cfg);
  },
};

/**
 * Beetle — an armoured ground SCAVENGER/grazer. It is NOT a hunter: it ignores
 * insects entirely (low direct threat) and instead competes with colonies for
 * FOOD. When a food seam is wired and a food node is in range it raids it
 * (adjacent) or closes on it (mirrors the Rat's food path, but `eatsInsects`
 * false). With no food sensed it drifts back toward home and roams there — a
 * slow, home-bound grazer, not a map-wide raider. Allocation-free.
 */
const BeetleBehavior: WildlifeBehavior = {
  species: SPECIES.Beetle,
  name: 'beetle',
  update(sys, id) {
    const e = sys.entities;
    const cfg = sys.cfgOf(id);
    const ax = e.posX[id] as number;
    const ay = e.posY[id] as number;

    // Scavenger: only food, never prey (eatsInsects is false in its config).
    const food =
      cfg.eatsFood && sys.hasFoodSeam() ? sys.senseFoodNode(ax, ay, cfg.senseRadius) : NIL;
    if (food !== NIL) {
      const fx = e.posX[food] as number;
      const fy = e.posY[food] as number;
      if (chebyshev(ax, ay, fx, fy) <= 1) {
        e.state[id] = WILDLIFE_STATE.Feeding;
        sys.eatFood(id, food, cfg);
      } else {
        e.state[id] = WILDLIFE_STATE.Migrating;
        sys.advance(id, fx, fy, cfg);
      }
      return;
    }

    // Nothing to graze: drift home (or wander one step when already near it).
    const hx = sys.homeXOf(id);
    const hy = sys.homeYOf(id);
    e.state[id] = WILDLIFE_STATE.Roaming;
    if (chebyshev(ax, ay, hx, hy) > cfg.roamRadius) sys.advance(id, hx, hy, cfg);
    else sys.wander(id, cfg);
  },
};

/**
 * Dragonfly — a fast, FLYING, TERRITORIAL aerial hunter. Unlike the Bird (which
 * migrates map-wide), it engages prey ONLY within `territoryRadius` of home and
 * otherwise patrols home; a darting, agile predator distinct from the Bird. High
 * stepsPerTick + flying make it fast and wall-agnostic, but its leash keeps it
 * local. Allocation-free.
 */
const DragonflyBehavior: WildlifeBehavior = {
  species: SPECIES.Dragonfly,
  name: 'dragonfly',
  update(sys, id) {
    const e = sys.entities;
    const cfg = sys.cfgOf(id);
    const ax = e.posX[id] as number;
    const ay = e.posY[id] as number;
    const hx = sys.homeXOf(id);
    const hy = sys.homeYOf(id);

    const prey = sys.sensePrey(ax, ay, cfg.senseRadius);
    if (prey !== NIL) {
      const px = e.posX[prey] as number;
      const py = e.posY[prey] as number;
      // TERRITORIAL: only strike prey inside the home territory; never chase out.
      if (chebyshev(hx, hy, px, py) <= cfg.territoryRadius) {
        e.state[id] = WILDLIFE_STATE.Hunting;
        if (chebyshev(ax, ay, px, py) <= 1) sys.strikePrey(id, prey, cfg);
        else sys.advance(id, px, py, cfg);
        return;
      }
    }
    // No engageable prey: patrol home (fly back, or wander one step when there).
    e.state[id] = WILDLIFE_STATE.Roaming;
    if (chebyshev(ax, ay, hx, hy) > cfg.roamRadius) sys.advance(id, hx, hy, cfg);
    else sys.wander(id, cfg);
  },
};

/**
 * Species-indexed dispatch table. Order MUST match the SPECIES.* values so
 * SPECIES_BEHAVIORS[species] is the right template. Shared by all wildlife.
 */
export const SPECIES_BEHAVIORS: readonly WildlifeBehavior[] = [
  SpiderBehavior, // SPECIES.Spider    = 0
  BirdBehavior, // SPECIES.Bird      = 1
  RatBehavior, // SPECIES.Rat       = 2
  FrogBehavior, // SPECIES.Frog      = 3
  BeetleBehavior, // SPECIES.Beetle    = 4
  DragonflyBehavior, // SPECIES.Dragonfly = 5
];
